import { ApiError, TransportError } from "@tdelivr/api-client";

/**
 * The states every screen here has to render, and the four the design does not draw.
 *
 * Both driver design files draw eighteen screens and not one of them is a loading, forbidden,
 * conflict or offline state. That is not an omission to work around silently: the rules say build
 * every designed state, and these four are states this application reaches and the design is silent
 * about. So each is rendered in the design's **own vocabulary** — the sheet, the state chip, the
 * title, the sub-line and at most one 56dp action — rather than in a look nobody drew, and the words
 * are this build's and flagged as such. Recorded as Q-59.
 *
 * Two of the six matter more here than they would on a console, and for reasons specific to a phone
 * in Dar es Salaam:
 *
 *   - **offline** — a courier loses signal in a car park. The design has a treatment for it, as a
 *     banner rather than a page: `6 Offline queue` draws an amber strip and a no-signal glyph. That
 *     screen is not built because the queued proof has nowhere to go, but the banner it draws is,
 *     and a failed request shows it.
 *   - **unconfirmable** — EXP-003's whole shape. `readyToGoOnline` and `rating.confirmed` and
 *     `earningsAbsence` are each a third value that is neither yes nor no, and the design supplies
 *     the pill for it: dashed, mono, carrying a `?`. A state is never shown by colour alone and is
 *     never shown as its nearest confident neighbour.
 */

export type ViewState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready" }
  /** The caller's token lacks a scope. Names it, because a courier told the name can ask for it. */
  | { readonly kind: "forbidden"; readonly missingScopes: readonly string[] }
  /** The request never reached a service. The design's own amber banner says so. */
  | { readonly kind: "offline" }
  | {
      readonly kind: "error";
      readonly code: string;
      readonly message: string;
      /** Whether offering a retry is honest. A button that cannot work teaches button-mashing. */
      readonly retryable: boolean;
    };

/**
 * Turn whatever went wrong into the state a screen can render.
 *
 * Exhaustive over the platform's eight error categories rather than a default case, so a category
 * added to the runtime fails the build here instead of being rendered as a generic failure.
 */
export function stateForError(error: unknown, missingScopes: readonly string[] = []): ViewState {
  if (error instanceof TransportError) return { kind: "offline" };

  if (!(error instanceof ApiError)) {
    return {
      kind: "error",
      code: "unexpected_error",
      message: error instanceof Error ? error.message : "Something went wrong",
      retryable: false,
    };
  }

  switch (error.category) {
    case "authorization":
    case "authentication":
      return { kind: "forbidden", missingScopes };
    case "not_found":
      /*
       * A 404 from EXP-003 is a fact about the courier rather than an empty list: there is no such
       * courier in this tenant. Drawn as an error naming that, because a blank screen would read as
       * "nothing is happening" to somebody waiting for work.
       */
      return {
        kind: "error",
        code: error.code,
        message: error.message,
        retryable: false,
      };
    case "conflict":
    case "validation":
    case "rate_limit":
    case "dependency":
    case "internal":
      return {
        kind: "error",
        code: error.code,
        message: error.message,
        retryable: error.retryable,
      };
  }
}

/** What to put on a retry control, if anything. */
export function retryLabel(state: ViewState): string | undefined {
  if (state.kind === "offline") return "Try again";
  if (state.kind === "error" && state.retryable) return "Try again";
  return undefined;
}
